import React, { useState, useRef, useEffect, useMemo } from 'react';

// Custom Searchable Dropdown Component
const SearchableSelect = ({ 
    value, 
    onChange, 
    options, 
    placeholder = "Select...", 
    renderOption = null,
    renderSelected = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    // Focus input and potentially pre-fill when opening
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            // Pre-fill if it's a custom value (has pipe or not in options)
            if (value && (String(value).includes('|') || !options.find(o => o.value === value))) {
                setSearchTerm(String(value));
            }
        }
    }, [isOpen, value, options]);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowerSearch = searchTerm.toLowerCase();
        return options.filter(opt => {
            // Skip group headers in search unless we want to search them? 
            // Usually we filter items. 
            // If item is a group header (disabled), maybe skip search filtering for it?
            // But filtering might remove its children.
            // Simplified: filter by label or value.
            if (opt.disabled) return false; // Don't match disabled headers directly

            return (opt.label && String(opt.label).toLowerCase().includes(lowerSearch)) ||
                   (opt.value && String(opt.value).toLowerCase().includes(lowerSearch));
        });
    }, [options, searchTerm]);

    const selectedOption = options.find(o => o.value === value);

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            {/* Input/Trigger Area */}
            <div 
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--background)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    minHeight: '38px',
                    color: 'var(--text-primary)'
                }}
            >
                {selectedOption ? (
                    renderSelected ? renderSelected(selectedOption) : (
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                             {selectedOption.label} 
                        </span>
                    )
                ) : (
                    value ? (
                         <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                              {value} 
                         </span>
                    ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>{placeholder}</span>
                    )
                )}
                <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>▼</span>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: '4px',
                    backgroundColor: 'var(--background)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
                        <input 
                            ref={inputRef}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Type to search..."
                            style={{
                                width: '100%',
                                padding: '6px',
                                borderRadius: 'var(--radius)',
                                border: '1px solid var(--border)',
                                fontSize: '0.875rem',
                                backgroundColor: 'var(--background)',
                                color: 'var(--text-primary)'
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchTerm) {
                                    e.preventDefault();
                                    handleSelect(searchTerm);
                                } else if (e.key === 'Escape') {
                                    setIsOpen(false);
                                }
                            }}
                            onClick={e => e.stopPropagation()} 
                        />
                    </div>
                    <div 
                        ref={listRef}
                        style={{ 
                            maxHeight: '250px', 
                            overflowY: 'auto',
                            padding: '4px 0'
                        }}
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => (
                                <div 
                                    key={opt.value || idx} 
                                    onClick={() => !opt.disabled && handleSelect(opt.value)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: opt.disabled ? 'default' : 'pointer',
                                        fontSize: '0.875rem',
                                        backgroundColor: !opt.disabled && opt.value === value ? 'var(--primary-light)' : (opt.disabled ? '#f9f9f9' : 'transparent'),
                                        borderLeft: !opt.disabled && opt.value === value ? '3px solid var(--primary)' : '3px solid transparent',
                                        color: opt.disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
                                        fontWeight: opt.disabled || opt.value === value ? '500' : '400'
                                    }}
                                    className={!opt.disabled ? "dropdown-item-hover" : ""}
                                    onMouseEnter={(e) => {
                                        if (!opt.disabled) e.currentTarget.style.backgroundColor = opt.value === value ? 'var(--primary-light)' : 'rgba(0,0,0,0.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!opt.disabled) e.currentTarget.style.backgroundColor = opt.value === value ? 'var(--primary-light)' : 'transparent';
                                    }}
                                >
                                    {renderOption ? renderOption(opt) : opt.label}
                                </div>
                            ))
                        ) : (
                            searchTerm && (
                                <div 
                                    onClick={() => handleSelect(searchTerm)}
                                    style={{
                                        padding: '12px',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        backgroundColor: 'var(--primary-light)',
                                        color: 'var(--primary)',
                                        fontWeight: '500',
                                        borderTop: '1px solid var(--border)'
                                    }}
                                >
                                    ✨ Use custom: "{searchTerm}"
                                </div>
                            )
                        )}
                        {filteredOptions.length === 0 && !searchTerm && (
                            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                No results found
                            </div>
                        )}
                        {searchTerm && filteredOptions.length > 0 && !filteredOptions.find(o => o.value === searchTerm) && (
                            <div 
                                onClick={() => handleSelect(searchTerm)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    borderTop: '1px solid var(--border)',
                                    color: 'var(--primary)',
                                    fontStyle: 'italic'
                                }}
                            >
                                ✨ Use custom: "{searchTerm}"
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
